// 设置中心「信用卡」：ETC 免费办理落地页的配置 + 提交记录（只有管理员，V3.3.26）。
// 判定规则只存服务器、不下发到落地页；卡号只存后四位。
import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../lib/api.js'
import { plainQr } from '../lib/poster.js'
import { formatBeijingTime } from '../lib/time.js'

const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']

export default function FreeApply() {
  const [cfg, setCfg] = useState(null)
  const [data, setData] = useState(null)
  const [banksText, setBanksText] = useState('')
  const [state, setState] = useState('')
  const [filter, setFilter] = useState('')   // '' | 'yes' | 'no'
  const [search, setSearch] = useState('')

  const inited = useRef(false)
  const seq = useRef(0)   // 只认最后一次请求的结果：快速切换筛选时，先发的请求晚回来不能盖掉新结果（Codex 300）
  async function load(f = filter, s = search) {
    const my = ++seq.current
    try {
      const d = await api.getFreeApply({ qualified: f === 'yes' ? true : f === 'no' ? false : undefined, search: s })
      if (my !== seq.current) return
      setData(d)
      // 表单只在第一次加载时填，刷新记录不能冲掉没保存的编辑（Codex 300）
      if (!inited.current) { inited.current = true; setCfg(d.config); setBanksText(d.config.banks.join('\n')) }
    } catch (e) { if (my === seq.current) setState(e.message) }
  }
  useEffect(() => { load() }, [])
  const pageUrl = data?.pageUrl || ''
  const qr = useMemo(() => (pageUrl ? plainQr(pageUrl) : ''), [pageUrl])   // 给客户扫的二维码（Gavin 2026-11-02：AI 接待让客户先扫码审核）

  if (!cfg) return <div className="muted">{state || '加载中…'}</div>
  const set = (k, v) => setCfg(x => ({ ...x, [k]: v }))
  const setRule = (k, v) => setCfg(x => ({ ...x, rule: { ...x.rule, [k]: v } }))
  const toggle = (list, d) => (list.includes(d) ? list.filter(x => x !== d) : [...list, d].sort())
  const toggleDigit = d => setRule('digits', toggle(cfg.rule.digits, d))
  // 规则 2：每一位单独启用、单独勾数字；所有启用的位都对上才合格
  const multi = cfg.rule.multi || []
  const cond = p => multi.find(c => c.position === p)
  const setCond = (p, on) => setRule('multi', on
    ? [...multi.filter(c => c.position !== p), { position: p, digits: [] }].sort((a, b) => a.position - b.position)
    : multi.filter(c => c.position !== p))
  const toggleCondDigit = (p, d) => setRule('multi', multi.map(c => (c.position === p ? { ...c, digits: toggle(c.digits, d) } : c)))
  const isAll = cfg.rule.mode === 'all'
  const rate = isAll
    ? (multi.length ? multi.reduce((a, c) => a * c.digits.length / 10, 1) * 100 : 0)
    : cfg.rule.digits.length * 10
  const rateText = +rate.toFixed(2)

  async function save() {
    setState('saving')
    try {
      const d = await api.setFreeApply({ ...cfg, banks: banksText.split('\n').map(s => s.trim()).filter(Boolean), waitSeconds: Number(cfg.waitSeconds) })
      setCfg(d.config); setBanksText(d.config.banks.join('\n'))
      setState('saved'); setTimeout(() => setState(''), 1800)
    } catch (e) { setState(e.message) }
  }
  async function contacted(r, v) {
    try { await api.markFreeApplyContacted(r.id, v); load() } catch (e) { setState(e.message) }
  }

  const field = (label, k, rows = 1, hint) => (
    <div className="field">
      <label>{label}</label>
      {rows > 1
        ? <textarea className="input" rows={rows} value={cfg[k]} onChange={e => set(k, e.target.value)} style={{ resize: 'vertical' }} />
        : <input className="input" value={cfg[k]} onChange={e => set(k, e.target.value)} />}
      {hint && <div className="small muted" style={{ marginTop: 4 }}>{hint}</div>}
    </div>
  )
  // 示例卡号尾号，让管理员一眼看懂「第几位」指哪一位
  const sample = '6217 •••• •••• 1234'.split('')
  const marked = new Set((isAll ? multi.map(c => c.position) : [cfg.rule.position]).map(p => sample.length - 4 + p - 1))
  const digitButtons = (list, onToggle) => DIGITS.map(d => (
    <button key={d} className="btn btn-sm" onClick={() => onToggle(d)} style={{ width: 40,
      ...(list.includes(d) ? { background: 'var(--brand)', borderColor: 'var(--brand)', color: '#fff' } : {}) }}>{d}</button>
  ))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div className="card" style={{ padding: 24 }}>
        <div className="small muted" style={{ marginBottom: 16, padding: 10, background: 'var(--hover)', borderRadius: 8 }}>
          客户打开落地页填写车牌、姓名、手机号、银行卡号、所属银行后提交，
          页面显示「正在核实」约 {cfg.waitSeconds} 秒后给出结果。只收 16 位信用卡（带校验位检查）。判定只在服务器做，客户看不到规则；卡号只存后四位。
          <div style={{ marginTop: 6 }}>落地页地址：{pageUrl
            ? <a href={pageUrl} target="_blank" rel="noreferrer">{pageUrl}</a>
            : <span>域名还没配好</span>}</div>
        </div>
        {qr && (
          <div className="row" style={{ gap: 16, marginBottom: 18, alignItems: 'center', flexWrap: 'wrap' }}>
            <img src={qr} alt="落地页二维码" width={150} height={150} style={{ border: '1px solid var(--line)', borderRadius: 8 }} />
            <div>
              <div className="small muted" style={{ marginBottom: 8 }}>客户扫这个码进入落地页。</div>
              <a className="btn btn-sm btn-primary" href={qr} download="ETC免费办理二维码.png" style={{ textDecoration: 'none' }}>下载二维码</a>
            </div>
          </div>
        )}

        <div style={{ fontWeight: 600, marginBottom: 10 }}>合格规则</div>
        <div className="row" style={{ gap: 18, flexWrap: 'wrap', marginBottom: 12 }}>
          <label className="row" style={{ gap: 6 }}><input type="radio" name="fa-mode" checked={!isAll} onChange={() => setRule('mode', 'one')} />规则 1：指定一位，中任意一个数字就合格</label>
          <label className="row" style={{ gap: 6 }}><input type="radio" name="fa-mode" checked={isAll} onChange={() => setRule('mode', 'all')} />规则 2：多个位置都要对上才合格</label>
        </div>
        {!isAll ? <>
          <div className="row" style={{ gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <span>卡号后四位的第</span>
            <select className="input" value={cfg.rule.position} onChange={e => setRule('position', Number(e.target.value))} style={{ width: 70 }}>
              {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>位是下面勾选的数字，就算合格</span>
          </div>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>{digitButtons(cfg.rule.digits, toggleDigit)}</div>
        </> : <>
          <div className="small muted" style={{ marginBottom: 8 }}>勾上要看的位置，再给每一位勾数字；勾上的每一位都对上才合格（一位勾多个数字，中其中一个就算这位对上）。</div>
          {[1, 2, 3, 4].map(p => (
            <div key={p} className="row fa-cond" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
              <label className="row" style={{ gap: 6, width: 90 }}><input type="checkbox" checked={!!cond(p)} onChange={e => setCond(p, e.target.checked)} />第 {p} 位</label>
              {cond(p) ? digitButtons(cond(p).digits, d => toggleCondDigit(p, d)) : <span className="small muted">不看这一位</span>}
            </div>
          ))}
        </>}
        <div className="small mono muted" style={{ margin: '6px 0' }}>例：{sample.map((c, i) => <span key={i} style={marked.has(i) ? { color: 'var(--brand)', fontWeight: 700, textDecoration: 'underline' } : {}}>{c}</span>)}（下划线是要看的位）</div>
        <div className="small fa-rate" style={{ marginBottom: 18, color: rate ? 'var(--ok)' : 'var(--danger)' }}>
          合格概率约 {rateText}%{!rate && (!isAll ? '（没勾数字，所有人都不合格）' : !multi.length ? '（没勾位置，所有人都不合格）' : '（有位置没勾数字，所有人都不合格）')}
        </div>

        <div style={{ fontWeight: 600, marginBottom: 10 }}>页面内容</div>
        {field('页面标题', 'title')}
        {field('底部注意事项（不填就不显示）', 'notice', 2)}
        {field('合格提示', 'passText', 2)}
        {field('不合格时弹窗直接显示的原因（换行会保留；不填就直接显示下面这句，不出「查看详情」）', 'failDetail', 4)}
        {field('不合格时「查看详情」里的文字', 'failText', 2)}
        <div className="field">
          <label>「正在核实」等待秒数</label>
          <input className="input" type="number" min="0" max="60" value={cfg.waitSeconds} onChange={e => set('waitSeconds', e.target.value)} style={{ width: 100 }} />
        </div>
        <div className="field">
          <label>所属银行下拉选项（一行一个，按顺序显示）</label>
          <textarea className="input" rows={8} value={banksText} onChange={e => setBanksText(e.target.value)} style={{ resize: 'vertical' }} />
        </div>
        <div className="row">
          <button className="btn btn-primary" onClick={save} disabled={state === 'saving'}>{state === 'saving' ? '保存中…' : '保存，立即生效'}</button>
          {state === 'saved' && <span className="small" style={{ color: 'var(--ok)' }}>已保存</span>}
          {state && state !== 'saving' && state !== 'saved' && <span className="small" style={{ color: 'var(--danger)' }}>{state}</span>}
        </div>
      </div>

      <div className="card" style={{ padding: 24 }}>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ fontWeight: 600 }}>提交记录</div>
          {data?.stats && <span className="small muted">共 {data.stats.total} 条 · 合格 {data.stats.qualified} · 今天 {data.stats.today}</span>}
          <div style={{ flex: 1 }} />
          <select className="input" value={filter} onChange={e => { setFilter(e.target.value); load(e.target.value, search) }} style={{ width: 110 }}>
            <option value="">全部</option><option value="yes">合格</option><option value="no">不合格</option>
          </select>
          <input className="input" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="车牌/姓名/手机号，回车搜索" style={{ width: 220 }} />
        </div>
        {data?.records?.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: 820 }}>
              <thead><tr><th>提交时间</th><th>车牌</th><th>姓名</th><th>手机号</th><th>银行</th><th>卡号</th><th>结果</th><th>跟进</th></tr></thead>
              <tbody>{data.records.map(r => (
                <tr key={r.id}>
                  <td className="small">{formatBeijingTime(r.created_at, { hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                  <td>{r.plate}</td><td>{r.name}</td><td className="mono">{r.phone}</td><td>{r.bank}</td>
                  <td className="mono">**** {r.card_last4}</td>
                  <td>{r.qualified ? <span className="tag tag-human">合格</span> : <span className="tag tag-closed">不合格</span>}</td>
                  <td>{r.contacted_at
                    ? <span className="small">已联系（{r.contacted_by}）<button className="btn-link small" onClick={() => contacted(r, false)}>撤销</button></span>
                    : <button className="btn-link small" onClick={() => contacted(r, true)}>标记已联系</button>}</td>
                </tr>
              ))}</tbody>
            </table>
            {data.records.length >= 200 && <div className="small muted" style={{ marginTop: 8 }}>只显示最新 200 条，用搜索找更早的。</div>}
          </div>
        ) : <div className="muted small">暂无提交记录。</div>}
      </div>
    </div>
  )
}
